
import { useState, useEffect, useCallback, memo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Library, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { useEnrollments } from "@/hooks/useEnrollments";
import { devLog } from "@/utils/devLog";
import { getCoursePlayerUrl, isCoursePlayerRoute } from "@/utils/routePaths";
import { performance } from "@/utils/performance";
import { useAuth } from "@/providers/AuthProvider";
import { Enrollment } from "@/types/enrollment";
import { useQueryClient } from "@tanstack/react-query";

// Enhanced memo implementation to avoid re-renders when navigating
export const SidebarCoursesList = memo(function SidebarCoursesList() {
  const location = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Enhanced logging for component lifecycle
  useEffect(() => {
    devLog(`[SidebarCoursesList] MOUNTED with path: ${location.pathname}`, { 
      timestamp: new Date().toISOString(),
      userId: user?.id 
    });
    
    return () => {
      devLog(`[SidebarCoursesList] UNMOUNTED from path: ${location.pathname}`, {
        timestamp: new Date().toISOString()
      });
    };
  }, []); // Only run on mount/unmount
  
  // Store the open state in local storage to persist across navigation
  const [isCoursesOpen, setIsCoursesOpen] = useState(() => {
    const saved = localStorage.getItem("sidebar_courses_open");
    // Default to open if no saved state or if we're on a course page
    return saved !== null ? saved === "true" : true;
  });
  
  // Use ref for performance tracking instead of state
  const perfMarkRef = useRef<ReturnType<typeof performance.start> | null>(null);
  const renderCountRef = useRef(0);
  
  // Track renders without causing re-renders
  renderCountRef.current += 1;
  
  useEffect(() => {
    devLog(`[SidebarCoursesList] Render count: ${renderCountRef.current}`);
    
    perfMarkRef.current = performance.start('sidebar-courses-list');
    
    return () => {
      if (perfMarkRef.current) {
        performance.end(perfMarkRef.current);
      }
    };
  }, []); // Only run on mount/unmount
  
  // Use the hook with userId if available, but with stale time configuration
  const { enrollments, isLoading } = useEnrollments(user?.id);
  
  // End performance measurement when data loads - only run effect when loading status changes
  useEffect(() => {
    if (!isLoading && perfMarkRef.current) {
      performance.end(perfMarkRef.current, "Enrollments loaded");
      perfMarkRef.current = null;
      
      devLog("[SidebarCoursesList] Enrollments loaded", {
        count: (enrollments as Enrollment[])?.length || 0,
        renderCount: renderCountRef.current
      });
    }
  }, [isLoading, enrollments]);
  
  // Update localStorage when state changes
  useEffect(() => {
    localStorage.setItem("sidebar_courses_open", isCoursesOpen.toString());
  }, [isCoursesOpen]);

  // Auto-expand courses section when on a course page (only run on initial mount or path change)
  useEffect(() => {
    const isCourse = isCoursePlayerRoute(location.pathname);
    
    if (isCourse && !isCoursesOpen) {
      devLog("[SidebarCoursesList] Auto-expanding courses section for course page");
      setIsCoursesOpen(true);
    }
  }, [location.pathname]); // Intentionally excluding isCoursesOpen to only expand automatically
  
  // Memoize the toggle handler
  const handleToggle = useCallback((e: React.MouseEvent) => {
    // Prevent the click from propagating to parent elements
    e.stopPropagation();
    e.preventDefault(); 
    setIsCoursesOpen(prev => !prev);
  }, []);

  // Handle course click to pre-fetch course data
  const handleCourseClick = useCallback((courseId: string, courseName: string) => {
    // Pre-warm the cache by telling React Query this query will be important soon
    // This doesn't cause a refetch if we have fresh data, but ensures smooth navigation
    queryClient.prefetchQuery({
      queryKey: ['coursePlayer', courseId],
      staleTime: 10 * 60_000 // 10 minutes
    });
    
    devLog("[SidebarCoursesList] Course clicked:", { courseId, courseName });
  }, [queryClient]);

  const enrollmentsArray = enrollments as Enrollment[] || [];
  const hasEnrollments = enrollmentsArray.length > 0;

  return (
    <div className="w-full">
      <Button 
        variant="ghost" 
        className="w-full justify-between font-normal group relative"
        onClick={handleToggle}
      >
        <div className="flex items-center gap-3">
          <Library size={20} />
          <span>My Courses</span>
        </div>
        {isCoursesOpen ? (
          <ChevronDown size={16} className="text-muted-foreground" />
        ) : (
          <ChevronRight size={16} className="text-muted-foreground" />
        )}
      </Button>
      
      {isCoursesOpen && (
        <div className="pl-9 pr-2 space-y-1 mt-1">
          {isLoading ? (
            <div className="py-2 text-sm text-muted-foreground flex items-center">
              <Loader2 className="h-3 w-3 mr-2 animate-spin" />
              Loading courses...
            </div>
          ) : hasEnrollments ? (
            <>
              {enrollmentsArray.map((enrollment) => {
                // Generate the correct course URL using utility
                const courseUrl = getCoursePlayerUrl(enrollment.course_id);
                
                const isActive = isCoursePlayerRoute(location.pathname, enrollment.course_id);
                
                return (
                  <Link 
                    key={enrollment.id} 
                    to={courseUrl}
                    className="block"
                    onClick={() => handleCourseClick(enrollment.course_id, enrollment.courses?.title || 'Course')}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "w-full justify-start py-1.5 h-auto text-sm",
                        isActive && "bg-primary/10 text-primary font-medium"
                      )}
                    >
                      <span className="truncate">{enrollment.courses?.title}</span>
                    </Button>
                  </Link>
                );
              })}
            </>
          ) : (
            <div className="py-2 text-sm text-muted-foreground">No enrolled courses</div>
          )}
        </div>
      )}
    </div>
  );
});
